import { toJS } from "mobx";
import queryString from "query-string";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

// ==== Stores ====
import AuthenticationStore from "stores/AuthenticationStore";
import OnboardingStore from "views/SignUp/Onboarding/store/OnboardingStore";
import FormsStore from "stores/FormsStore";

// ==== Types ====
import { ELocalStorageKeys, FEATURE_FLAGS } from "types/globalTypes";
import { EQueryStringValues } from "views/SignUp/SelfEnroll/types/selfEnrollTypes";
import { EOnboardingScreens } from "views/SignUp/Onboarding/types/onboardingTypes";

// ==== Utilities ====
import { ai } from "configuration/appInsights";

export const generateBaseUrl = (): string => {
  let untrimmedUrl = toJS(AuthenticationStore?.config?.torchUri);

  if (!untrimmedUrl) {
    FormsStore.setErrorHandler("generateBaseUrl", "No untrimmed url");

    // DEV MAGIC
    untrimmedUrl = "https://vulcan.dev.stonex.com/torchapi/";
  }

  // TODO - Need to remove this from the API
  return untrimmedUrl.replace("torchapi/", "");
};

export const setQueryStringsIntoState = (search: any): void => {
  // Small hack - If the user is making the return trip from the auth flow, we need to grab their email
  // so we can auto log them in and pre-pop their email.
  // This code only exists here because this all use to be in the url
  const storedEmail = localStorage.getItem(ELocalStorageKeys.USER_EMAIL);
  if (storedEmail) {
    AuthenticationStore.setAuthEmail(storedEmail);
    localStorage.removeItem(ELocalStorageKeys.USER_EMAIL);
  }

  // ==== PrivateLabelCode ====
  // Small cheat, sometimes privateLabelCode comes back as all lowercase and we are
  // checking for that here
  const privateLabelCode =
    getQueryString(search, "privatelabelcode") ||
    getQueryString(search, EQueryStringValues.PRIVATE_LABEL_CODE);

  if (privateLabelCode) {
    AuthenticationStore.setPrivateLabelCode(privateLabelCode);
  }

  // ==== AccountNumber ====
  // If they have an account pre-selected
  const urlAccountNumber = getQueryString(
    search,
    EQueryStringValues.ACCOUNT_NUMBER,
  );

  if (urlAccountNumber) {
    AuthenticationStore.setUrlAccountNumber(urlAccountNumber);
  }

  // ==== State ====
  const state = getQueryString(search, EQueryStringValues.STATE);

  if (state) {
    AuthenticationStore.setUrlState(state);
  }

  // ==== Email ====
  const email = getQueryString(search, EQueryStringValues.EMAIL);

  if (email) {
    AuthenticationStore.setAuthEmail(email);
  }

  // ==== Profile Id ====
  const profileId = getQueryString(search, EQueryStringValues.PROFILE_ID);

  if (profileId) {
    AuthenticationStore.setUrlProfileId(profileId);
  }

  // ==== Redirect Url ====
  const redirectUrl = getQueryString(search, EQueryStringValues.REDIRECT_URL);

  if (redirectUrl) {
    AuthenticationStore.setUrlRedirectUrl(redirectUrl);
  }

  // ==== Google Tag Manager Key ====
  const gtmKey = getQueryString(search, EQueryStringValues.GOOGLE_TAG_MANAGER);

  if (gtmKey) {
    AuthenticationStore.setGtmKey(gtmKey);
  }
};

export const getQueryString = (search: any, searchValue: string): string => {
  const values = queryString.parse(search);
  const foundQueryStringValue = values[searchValue];

  if (Array.isArray(foundQueryStringValue)) {
    return foundQueryStringValue[0];
  }

  return foundQueryStringValue;
};

// For all existing URL search params, add them to the url
export const generateUrlWithExistingQueryStrings = (
  baseUrl: string,
): string => {
  let urlParams = [];

  // Don't pass email to query string as it's PII info in the url
  if (AuthenticationStore.urlPrivateLabelCode) {
    urlParams.push({
      key: EQueryStringValues.PRIVATE_LABEL_CODE,
      value: AuthenticationStore.urlPrivateLabelCode,
    });
  }
  if (AuthenticationStore.urlState) {
    urlParams.push({
      key: EQueryStringValues.STATE,
      value: AuthenticationStore.urlState,
    });
  }
  if (AuthenticationStore.urlAccountNumber) {
    urlParams.push({
      key: EQueryStringValues.ACCOUNT_NUMBER,
      value: AuthenticationStore.urlAccountNumber,
    });
  }
  if (AuthenticationStore.urlRedirectUrl) {
    urlParams.push({
      key: EQueryStringValues.REDIRECT_URL,
      value: AuthenticationStore.urlRedirectUrl,
    });
  }

  let url = baseUrl;

  urlParams.forEach(({ key, value }, index) => {
    if (index === 0) {
      url = `${url}?${key}=${value}`;
    } else {
      url = `${url}&${key}=${value}`;
    }
  });

  return url;
};

export const injectDevToken = (): void => {
  const devTokenData = AuthenticationStore.getDevToken();

  if (devTokenData && !AuthenticationStore?.newUserCreationFormData?.email) {
    // Inject data into login form
    AuthenticationStore.updateLoginFormData({
      ConfirmEmail: devTokenData.email,
      Email: devTokenData.email,
    });

    // Inject data into New User Creation form
    AuthenticationStore.updateNewUserCreationFormData({
      confirmEmail: devTokenData.email,
      email: devTokenData.email,
      firstName: devTokenData.firstName,
      lastName: devTokenData.lastName,
    });

    OnboardingStore.setFormItemData(EOnboardingScreens.INDIVIDUAL, {
      ConfirmEmail: devTokenData.email,
      Email: devTokenData.email,
      FirstName: devTokenData.firstName,
      LastName: devTokenData.lastName,
    });
  }
};

export const debounce = (func: any, wait: number) => {
  let timeout: ReturnType<typeof setTimeout>;
  const returnFunction = (...args: any) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
  return returnFunction;
};

const REDACTED = "[REDACTED]";
const SENSITIVE_KEYS = [
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-access-token",
  "accesstoken",
  "access_token",
  "idtoken",
  "id_token",
  "refreshtoken",
  "refresh_token",
  "jwt",
  "token",
  "bearertoken",
  "bearer_token",
  "clientsecret",
  "client_secret",
];
// JWT shape: base64url "header.payload.signature"; a JSON header starting with {" always encodes to "eyJ".
// The leading group stands in for a lookbehind (unsupported by some target browsers): a match must start at a token boundary,
// which keeps scanning linear on long inputs. In URL-encoded text that boundary is a "%XX" escape (e.g. "%3DeyJ...").
const JWT_PATTERN =
  /(^|[^A-Za-z0-9_-]|%[0-9A-Fa-f]{2})eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g;
// "Bearer <token>" incl. URL-encoded space; 16+ chars so prose like "Bearer token missing" is left alone.
// Starts at a boundary that may be a "%XX" escape, as in a URL-encoded header ("Authorization%3A%20Bearer%20...").
const BEARER_PATTERN =
  /(^|[^A-Za-z0-9_]|%[0-9A-Fa-f]{2})(bearer(?:\s|%20)+)[A-Za-z0-9\-._~+/]{16,}=*/gi;
// "Basic <base64 credentials>": must contain a digit or + / = so ordinary words aren't redacted.
const BASIC_PATTERN =
  /\b(basic(?:\s|%20)+)(?=[A-Za-z0-9+/]*[0-9+/=])[A-Za-z0-9+/]{8,}={0,2}/gi;
// access_token=, id_token=, refresh_token= in query strings (also opaque, non-JWT tokens), including URL-encoded ones
// ("%3Fid_token%3D..."), whose value ends at an encoded "&" (%26).
const TOKEN_QUERY_PATTERN =
  /(^|[^A-Za-z0-9_]|%[0-9A-Fa-f]{2})((?:access|id|refresh)_token(?:=|%3D))(?:(?!%26)[^&#\s"'])+/gi;
// Token-named properties inside JSON that is already a string (e.g. axios config.data request bodies).
const JSON_TOKEN_PROPERTY_PATTERN =
  /("(?:authorization|access_?token|id_?token|refresh_?token|jwt|token|bearer_?token|client_?secret)"\s*:\s*")(?:[^"\\]|\\.)*(")/gi;
// The same properties in JSON nested inside a JSON string, where the quotes are escaped: \"token\":\"...\".
const ESCAPED_JSON_TOKEN_PROPERTY_PATTERN =
  /("(?:authorization|access_?token|id_?token|refresh_?token|jwt|token|bearer_?token|client_?secret)\\+"\s*:\s*\\+")(?:[^"\\]|\\+[^"\\])*(\\+")/gi;

const redactString = (value: string): string =>
  value
    .replace(JSON_TOKEN_PROPERTY_PATTERN, `$1${REDACTED}$2`)
    .replace(ESCAPED_JSON_TOKEN_PROPERTY_PATTERN, `$1${REDACTED}$2`)
    .replace(TOKEN_QUERY_PATTERN, `$1$2${REDACTED}`)
    .replace(BEARER_PATTERN, `$1$2${REDACTED}`)
    .replace(BASIC_PATTERN, `$1${REDACTED}`)
    .replace(JWT_PATTERN, `$1${REDACTED}`);

export const sanitizeError = (input: unknown): unknown => {
  try {
    if (typeof input === "string") return redactString(input);
    if (!input || typeof input !== "object") return input;
    // JSON round trip mirrors what App Insights does with properties, honours toJSON() on AxiosError/AxiosHeaders,
    // and lets the replacer see every key.
    return JSON.parse(
      JSON.stringify(input, (key: string, value: unknown) => {
        if (SENSITIVE_KEYS.includes(key.toLowerCase())) return REDACTED;
        if (typeof value === "string") return redactString(value);
        return value;
      }),
    );
  } catch {
    // Fail closed: never send something we could not sanitize (e.g. circular structures).
    return "[REDACTED: unserializable]";
  }
};

// A flag absent from App Configuration reads as off (tokens logged as before). App Insights is only initialised
// once /l/config has loaded, so flag values are always known by the time anything is tracked.
export const isBearerTokenLoggingSuppressed = (): boolean => {
  try {
    return AuthenticationStore.getFeatureFlag(
      FEATURE_FLAGS.SUPPRESS_BEARER_TOKEN_LOGGING,
    );
  } catch {
    // Fail closed only when the flag cannot be read at all.
    return true;
  }
};

export const handleErrorTracking = (
  e: unknown,
  fileName: string,
  functionName: string,
): void => {
  ai?.appInsights?.trackException({
    error: new Error(`ERROR: ${fileName} ${functionName}`),
    properties: {
      apiResponse: isBearerTokenLoggingSuppressed() ? sanitizeError(e) : e,
    },
    severityLevel: SeverityLevel.Error,
  });
};
